import { FormFieldType, TCustomField, TFormGroup, TGroupedFormDef } from 'src/types';

import {
  authorizationLevelOptions,
  boardExamOptions,
  checkStatementOptions,
  confirmationOptions,
  defaultFieldProps,
  fullFieldProps,
  schoolOptions,
  schoolTypeOptions,
  SchoolTypes,
  accreditedByOptions,
  academicDisciplineOptions,
  primaryTeachOptions,
  testMaterialBuildingOptions,
  collegeBoardOfferingsOptions,
  satAdminDateOptions
} from 'src/constants';
import {
  checkAcademicDiscipline,
  checkAdministeredTest,
  checkHsAward,
  checkPrimaryTeach,
  checkSchoolType,
  checkSchoolUsedName,
  checkTestSecurity,
  checkSchoolAccredition,
  emailPattern,
  getAddressFields,
  positiveNumber,
  positiveWholeNumber,
  checkCollegeBoardOfferings,
} from 'src/utils';

const studentGroupFieldProps = {
  lg: 2,
  validator: {
    validate: positiveWholeNumber,
    validationMessage: 'Must be a positive whole number.',
  },
};

const studentDefaultFields = (prefix: string, type = 'Enrolled'): TCustomField[] => [
  {
    label: 'K:',
    path: `${prefix}.zero${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '1st:',
    path: `${prefix}.one${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '2nd:',
    path: `${prefix}.two${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '3rd:',
    path: `${prefix}.three${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '4th:',
    path: `${prefix}.four${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '5th:',
    path: `${prefix}.five${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '6th:',
    path: `${prefix}.six${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '7th:',
    path: `${prefix}.seven${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '8th:',
    path: `${prefix}.eight${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '9th:',
    path: `${prefix}.nineMs${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
    renderCondition: (fields) => fields.schoolType === SchoolTypes.MS,
  },
  {
    label: '9th:',
    path: `${prefix}.nine${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
    renderCondition: (fields) => fields.schoolType !== SchoolTypes.MS,
  },
  {
    label: '10th:',
    path: `${prefix}.ten${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '11th:',
    path: `${prefix}.eleven${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
  {
    label: '12th:',
    path: `${prefix}.twelve${type}`,
    type: FormFieldType.NUMBER,
    required: true,
    ...studentGroupFieldProps,
  },
];

const defaultCheckOptionsLayout = {
  xl: 3,
  md: 4,
  xs: 6,
};

export const schoolForms: TGroupedFormDef = {
  title: 'Basic school form',
  groups: [
    {
      groupName: 'School Selection',
      fields: [
        {
          label: 'School Type:',
          path: 'schoolType',
          type: FormFieldType.RADIO_GROUP,
          options: schoolTypeOptions,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Authorization Level:',
          path: 'level',
          type: FormFieldType.RADIO_GROUP,
          options: authorizationLevelOptions,
          required: true,
          ...fullFieldProps,
        },
      ],
    },
    {
      groupName: 'School',
      fields: [
        {
          label: 'Organization Name:',
          path: 'schoolName',
          placeholder: 'School/Entity Name',
          type: FormFieldType.TEXT,
          required: true,
          ...defaultFieldProps,
        },
      ],
    },
    {
      groupName: 'Visiting Address',
      showGroupName: true,
      fields: getAddressFields('visitingAddress'),
    },
    {
      groupName: 'Shipping Address(if different from Visiting)',
      isExpandable: true,
      fields: getAddressFields('shippingAddress'),
    },
    {
      groupName: 'Contact',
      fields: [
        {
          label: 'Telephone number:',
          path: 'phoneNumber',
          placeholder: 'Telephone number',
          type: FormFieldType.TEXT,
          required: true,
          ...defaultFieldProps,
        },
        {
          label: 'Fax number:',
          path: 'faxNumber',
          placeholder: 'Fax number',
          type: FormFieldType.TEXT,
          ...defaultFieldProps,
        },
        {
          label: 'Organization Website:',
          path: 'webSite',
          placeholder: 'Web site',
          type: FormFieldType.TEXT,
          required: true,
          ...defaultFieldProps,
        },
        {
          label: 'Email:',
          path: 'schoolEmail',
          placeholder: 'Email',
          type: FormFieldType.TEXT,
          required: true,
          validator: {
            required: 'Your email is required.',
            pattern: {
              value: emailPattern,
              message: 'Please enter your email address in the correct format.',
            },
          },
          ...defaultFieldProps,
        },
        {
          label: 'Are you a member of a school district/school network?',
          path: 'memberOfDistrict',
          type: FormFieldType.RADIO_GROUP,
          required: true,
          options: confirmationOptions,
          ...defaultFieldProps,
        },
        {
          label: 'Do you share address with another organization?',
          path: 'shareAddress',
          type: FormFieldType.RADIO_GROUP,
          required: true,
          options: confirmationOptions,
          ...defaultFieldProps,
        },
        {
          label: 'List the school district',
          path: 'schoolDistricts',
          type: FormFieldType.TEXT,
          placeholder: 'District',
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.memberOfDistrict === 'yes',
        },
        {
          label: 'List the school',
          path: 'shareAddressSchools',
          type: FormFieldType.TEXT,
          placeholder: 'School List',
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.shareAddress === 'yes',
        },
      ],
    },
    {
      groupName: 'School Information',
      fields: [
        {
          label: 'Established Date:',
          path: 'schoolEstDate',
          placeholder: 'Select a date...',
          type: FormFieldType.DATE,
          ...defaultFieldProps,
        },
      ],
    },
    {
      groupName: 'School Usage',
      fields: [
        {
          label: 'Has your organization ever used another name,address,or College Board school code?',
          path: 'usedName',
          type: FormFieldType.RADIO_GROUP,
          required: true,
          options: confirmationOptions,
          validator: {
            validate: checkSchoolUsedName,
            validationMessage: "Required, if select 'Yes', provide one of the follow fields.",
          },
          ...fullFieldProps,
        },
        {
          label: 'Enter old school information here',
          path: 'usedNameSchool',
          type: FormFieldType.TEXT,
          placeholder: 'School',
          ...defaultFieldProps,
          renderCondition: (field) => field.usedName === 'yes',
        },
        {
          label: 'If a merger, list all schools/codes affected',
          path: 'usedNameMergeSchools',
          type: FormFieldType.TEXT,
          placeholder: 'School',
          ...defaultFieldProps,
          renderCondition: (field) => field.usedName === 'yes',
        },
      ],
    },
    {
      groupName: 'School Type',
      fields: [
        {
          label: 'Type of School: (check all that apply) ',
          path: 'type',
          type: FormFieldType.CHECK_GROUP,
          options: schoolOptions,
          optionsLayout: defaultCheckOptionsLayout,
          validator: {
            validate: checkSchoolType,
            validationMessage: 'Required - please check all that apply.',
          },
          ...fullFieldProps,
        },
        {
          label: 'Submit explanation',
          path: 'type.schoolTypeOtherExplanation',
          type: FormFieldType.TEXT,
          required: true,
          ...fullFieldProps,
          renderCondition: (field) => field.type?.schoolTypeOther === 'yes',
        },
      ],
    },
    {
      groupName: 'Enter number of students enrolled in each grade:',
      showGroupName: true,
      fields: [...studentDefaultFields('studentEnrolled')],
    },
    {
      groupName: 'Check statements that apply:',
      fields: [
        {
          label: 'Check statements that apply:*',
          path: '',
          type: FormFieldType.CHECK_GROUP,
          required: true,
          options: checkStatementOptions,
          optionsLayout: { lg: 6 },
          validator: {
            validate: checkHsAward,
            validationMessage: 'Required - please check all that apply.',
          },
          ...fullFieldProps,
        },
        {
          label: 'Provide name of other awards',
          path: 'hsAwardOtherText',
          type: FormFieldType.TEXT,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.hsAwardOther === 'yes',
        },
      ],
    },
    {
      groupName: 'Number of students taught on site:',
      showGroupName: true,
      fields: [
        ...studentDefaultFields('onsiteStudent', 'Taught'),
        {
          label:
            'On average, how many days and total hours per week are students required to report on-site for instruction?',
          path: 'onsiteStudent',
          required: true,
          type: FormFieldType.SINGLE_INSTANCE,
          properties: [
            {
              path: 'onsiteTaughtDay',
              placeholder: 'Days per week',
              required: true,
              type: FormFieldType.NUMBER,
              lg: 3,
              xs: 3,
              step: 'any',
              validator: {
                validate: positiveNumber,
                validationMessage: "Must be a number greater than 0."
              }
            },
            {
              path: 'onsiteTaughtHour',
              placeholder: 'Total Hours per week',
              required: true,
              type: FormFieldType.NUMBER,
              lg: 3,
              xs: 3,
              step: 'any',
              validator: {
                validate: positiveNumber,
                validationMessage: "Must be a number greater than 0."
              }
            },
          ],
          lg: 12,
        },
      ],
      renderCondition: (field) => field.level === '2',
    },
    {
      groupName: 'Test prep',
      fields: [
        {
          label: 'Do you hold test prep?',
          path: 'holdTestPrep',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          required: true,
          xs: 12,
        },
        {
          label: 'Please further explain your College Board exam preparation. When are preparation courses held?',
          path: 'testPrepExplain',
          type: FormFieldType.TEXT,
          xs: 12,
          renderCondition: (field) => field.holdTestPrep === 'yes',
        },
        {
          label: 'Is the test preparation only for students enrolled in the organization submitting this code request?',
          path: 'testPrepEnrolled',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          xs: 12,
          renderCondition: (field) => field.holdTestPrep === 'yes',
        },
        {
          label:
            'Does the school or any teacher involved in the College Board exam preparation receive compensation for these services outside of their normal salary?',
          path: 'testPrepTeacherInvolved',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          xs: 12,
          renderCondition: (field) => field.holdTestPrep === 'yes',
        },
      ],
      renderCondition: (field) => field.level === '2',
    },
    {
      groupName: 'College Board offerings:',
      fields: [
        {
          label: 'Please indicate which of the following College Board offerings your institution is interested in offering (Check all that apply):*',
          path: 'collegeBoardOfferings',
          type: FormFieldType.CHECK_GROUP,
          required: true,
          options: collegeBoardOfferingsOptions,
          optionsLayout: { xs: 3 },
          validator: {
            validate: checkCollegeBoardOfferings,
            validationMessage: 'Required - please check all that apply.',
          },
          ...fullFieldProps,
        },
        {
          label: 'Please select the earliest administration date your institution is interested in below. ',
          path: 'collegeBoardOfferings.satAdminDate',
          type: FormFieldType.SELECT,
          placeholder: 'Select one',
          options: satAdminDateOptions,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) =>
            field.collegeBoardOfferings?.satTestCenter === 'yes' ,
        },
      ],
    },
    {
      groupName: 'AP Course Offer',
      fields: [
        {
          label: 'Are you interested in offering AP courses?',
          path: 'apCourseOffer',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          required: true,
          xs: 12,
        },
        {
          label: (
            <div>
              Please confirm your organization will offer AP courses in accordance with the appropriate grade levels
              policy :
              <a
                href="https://secure-media.collegeboard.org/digitalServices/pdf/ap/appropriate-grade-levels-for-ap-courses.pdf"
                target="_blank"
                rel="noreferrer">
                Appropriate AP Courses Grade Levels
              </a>
            </div>
          ) as unknown as string,
          path: 'apCourseOfferConfirmation',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          xs: 12,
          renderCondition: (field) => field.apCourseOffer === 'yes',
        },
        {
          label: 'Please list the AP courses you wish to offer',
          path: 'apCourseOfferList',
          type: FormFieldType.TEXT,
          xs: 12,
          renderCondition: (field) => field.apCourseOffer === 'yes',
        },
      ],
    },
    {
      groupName: 'School Accredition',
      fields: [
        {
          label: 'Is your school accredited by any of the following agencies/organizations?',
          comment:
            '(Use the Add document option to provide the accreditation certificate and/or for other accreditations)',
          path: 'approvedAccreditation.cbAccredited',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
        },
        {
          label: '',
          type: FormFieldType.SINGLE_INSTANCE,
          path: 'approvedAccreditation',
          properties: [
            {
              label: 'Select',
              path: 'cbAccreditedBy',
              placeholder: 'Select one',
              type: FormFieldType.SELECT,
              options: accreditedByOptions,
              required: true,
              lg: 6,
              xs: 6,
            },
            {
              label: 'Expiration date:',
              path: 'cbAccreditedDate',
              type: FormFieldType.DATE,
              required: true,
              lg: 6,
              xs: 6,
            },
          ],
          validator: {
            validate: checkSchoolAccredition,
            validationMessage: 'Required if your school is accredited.',
          },
        },
        {
          label: 'Are you accredited by any other agency/organization?',
          type: FormFieldType.RADIO_GROUP,
          path: 'approvedAccreditation.agencyAccredited',
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
          renderCondition: (field) => field.approvedAccreditation?.cbAccredited === 'no',
        },
        {
          label: 'Please note the agency/organization:',
          type: FormFieldType.TEXT,
          path: 'approvedAccreditation.agencyAccreditedName',
          required: true,
          ...fullFieldProps,
          renderCondition: (field) =>
            field.approvedAccreditation?.cbAccredited !== 'yes' &&
            field.approvedAccreditation?.agencyAccredited === 'yes',
        },
        {
          label: 'Please upload supporting materials and authorized submitter documentation(if applicable). Please be sure to add a description to complete the upload. Acceptable formats are jpg, png, and pdf with a max file size of 5MB.',
          type: FormFieldType.FILE_UPLOAD,
          uploadButton: 'Upload file',
          path: '',
          ...fullFieldProps,
        },
      ],
      renderCondition: (field) => field.level === '2',
    },
    {
      groupName: 'Total number of teachers employed',
      showGroupName: true,
      fields: [
        {
          label: 'Full-time',
          path: 'teachers.hsFullTimeTeachers',
          type: FormFieldType.NUMBER,
          required: true,
          lg: 6,
          xs: 12,
          validator: {
            validate: positiveWholeNumber,
            validationMessage: 'Must be a positive whole number.',
          },
        },
        {
          label: 'Part-time',
          path: 'teachers.hsPartTimeTeachers',
          required: true,
          type: FormFieldType.NUMBER,
          lg: 6,
          xs: 12,
          validator: {
            validate: positiveWholeNumber,
            validationMessage: 'Must be a positive whole number.',
          },
        },
      ],
      renderCondition: (field) => field.level === '2',
    },
    {
      groupName: 'Students Relatives',
      fields: [
        {
          label: 'Any relatives or household members of students employed?',
          path: 'relatives.relativeEmployed',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'How many teachers?',
          path: 'relatives.relativeEmployedTeachers',
          type: FormFieldType.NUMBER,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.relatives?.relativeEmployed === 'yes',
          validator: {
            validate: positiveWholeNumber,
            validationMessage: 'Must be a positive whole number.'
          }
        },
        {
          label: 'How many administrators?',
          path: 'relatives.relativeEmployedAdministrators',
          type: FormFieldType.NUMBER,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.relatives?.relativeEmployed === 'yes',
          validator: {
            validate: positiveWholeNumber,
            validationMessage: 'Must be a positive whole number.'
          }
        },
      ],
      renderCondition: (field) => field.level === '2',
    },
    {
      groupName: 'Academic disciplines offered:',
      fields: [
        {
          label: 'Academic disciplines offered:',
          path: 'academicDiscipline',
          type: FormFieldType.CHECK_GROUP,
          options: academicDisciplineOptions,
          optionsLayout: { xs: 3 },
          required: true,
          validator: {
            validate: checkAcademicDiscipline,
            validationMessage: 'Required - please check all that apply.',
          },
          ...fullFieldProps,
        },
        {
          label: 'Please list',
          path: 'academicDiscipline.academicDisciplineOtherName',
          type: FormFieldType.TEXT,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.academicDiscipline?.academicDisciplineOther === 'yes',
        },
      ],
    },
    {
      groupName: 'College Board exams',
      fields: [
        {
          label: 'School previously administered College Board exams:',
          path: 'administeredTest',
          type: FormFieldType.CHECK_GROUP,
          options: boardExamOptions,
          optionsLayout: { xs: 2 },
          required: true,
          validator: {
            validate: checkAdministeredTest,
            validationMessage: 'Required - please check all that apply.',
          },
          ...fullFieldProps,
        },
        {
          label: 'Please enter the date of the most recent administration for these exams',
          path: 'administeredTest.administeredTestDate',
          type: FormFieldType.DATE,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) =>
            field.administeredTest?.administeredTestAp === 'yes' ||
            field.administeredTest?.administeredTestPsat === 'yes' ||
            field.administeredTest?.administeredTestSat === 'yes' ||
            field.administeredTest?.administeredTestOther === 'yes',
        },
        {
          label: 'SAT Test Center ID # if applicable',
          path: 'administeredTest.administeredTestOtherName',
          placeholder: 'SAT Test Center ID # if applicable',
          type: FormFieldType.TEXT,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.administeredTest?.administeredTestOther === 'yes',
        },
      ],
      renderCondition: (field) => field.level === '2',
    },
    {
      groupName: 'School primarily teaches:',
      fields: [
        {
          label: 'School primarily teaches:',
          path: 'primaryTeach',
          type: FormFieldType.CHECK_GROUP,
          options: primaryTeachOptions,
          optionsLayout: { xs: 3 },
          required: true,
          validator: {
            validate: checkPrimaryTeach,
            validationMessage: 'Required - please check all that apply.',
          },
          ...fullFieldProps,
        },
        {
          label: 'Please explain',
          path: 'primaryTeach.teachOtherName',
          placeholder: 'Please explain',
          type: FormFieldType.TEXT,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.primaryTeach?.teachOther === 'yes',
        },
      ],
      renderCondition: (field) => field.level === '2',
    },
    {
      groupName: 'Test Security Questions:',
      fields: [
        {
          label: 'Will testing be held at the visiting address provided?',
          path: 'security.testAtVisitingAddress',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Will testing be held at the shipping address?',
          path: 'security.testAtAddress',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
          renderCondition: (field) => field.security?.testAtVisitingAddress === 'no',
        },
        {
          label: 'Test material received by:',
          path: 'security.testMaterialReceivedBy',
          placeholder: 'Name & Title',
          type: FormFieldType.TEXT,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Where will test material be received (in building)?',
          path: 'security',
          type: FormFieldType.CHECK_GROUP,
          options: testMaterialBuildingOptions,
          optionsLayout: { xs: 2 },
          required: true,
          validator: {
            validate: checkTestSecurity,
            validationMessage: 'Required - please check all that apply.',
          },
          ...fullFieldProps,
        },
        {
          label: 'Please Specify',
          path: 'security.receivedOtherName',
          type: FormFieldType.TEXT,
          required: true,
          ...fullFieldProps,
          renderCondition: (field) => field.security?.receivedOther === 'yes',
        },
        {
          label: 'Where will material be stored?',
          path: 'security.materialsStored',
          type: FormFieldType.TEXT,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Can storage be locked?',
          path: 'security.storageLocked',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Name of person responsible for security:',
          path: 'security.responsiblePerson',
          type: FormFieldType.TEXT,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Any non-employed persons have access?',
          path: 'security.nonEmployedAccess',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Does your location have designated rooms for testing?',
          path: 'security.testingRoomsExist',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Do designated testing rooms have power and WIFI access?',
          path: 'security.testingRoomsPowerWifi',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          optionsLayout: { xs: 2 },
          required: true,
          ...fullFieldProps,
          renderCondition: (field) => field.security?.testingRoomsExist === 'yes',
        },
      ],
      renderCondition: (field) => field.level === '2',
    },
  ],
};

export const formSubmitGroup: TFormGroup = {
  groupName: 'Submit Group',
  fields: [
    {
      label:
        'By submitting this request I confirm that all of the information provided is true and accurately describes the organization named on this form and have the authority to make this request. I understand that if any of the information is false, deactivation of the school code and/or legal action may result.',
      path: '',
      type: FormFieldType.CHECK_GROUP,
      options: [{ value: 'disclaimer', label: 'Accept' }],
      required: true,
      ...fullFieldProps,
    },
    {
      label: 'Name',
      path: 'submitter',
      type: FormFieldType.TEXT,
      required: true,
      ...fullFieldProps,
    },
  ],
};

export const defaultValues = {
  schoolType: SchoolTypes.HS,
  level: '1',
  schoolName: '',

  printStatus: false,

  visitingAddress: {
    address1: '',
    address2: '',
    address3: '',
    city: '',
    state: '',
    zip: '',
    county: '',
    stateOrProvince: '',
    country: '',
    internationalPostalCode: '',
  },
  shippingAddress: {
    address1: '',
    address2: '',
    address3: '',
    city: '',
    state: '',
    zip: '',
    county: '',
    stateOrProvince: '',
    country: '',
    internationalPostalCode: '',
  },
  phoneNumber: '',
  faxNumber: '',
  webSite: '',
  schoolEmail: '',
  memberOfDistrict: '',
  schoolDistricts: '',
  shareAddress: '',
  shareAddressSchools: '',
  usedName: '',
  usedNameSchool: '',
  usedNameMergeSchools: '',

  type: {
    schoolTypePublic: '',
    schoolTypePrivateRel: '',
    schoolTypeCatholic: '',
    schoolTypePrivate: '',
    schoolTypeYouth: '',
    schoolTypeCharter: '',
    schoolTypeHome: '',
    schoolTypeOnline: '',
    schoolTypeDefense: '',
    schoolTypeIndianAffairs: '',
    schoolTypeOther: '',
    schoolTypeOtherExplanation: '',
  },

  studentEnrolled: {
    zeroEnrolled: '',
    oneEnrolled: '',
    twoEnrolled: '',
    threeEnrolled: '',
    fourEnrolled: '',
    fiveEnrolled: '',
    sixEnrolled: '',
    sevenEnrolled: '',
    eightEnrolled: '',
    nineMsEnrolled: '',
    nineEnrolled: '',
    tenEnrolled: '',
    elevenEnrolled: '',
    twelveEnrolled: '',
    addTenth: '',
  },

  hsDiploma: '',
  grantCredit: '',
  hsDiplomaEquivalency: '',
  hsAwardOther: '',
  hsAwardOtherText: '',

  onsiteStudent: {
    fiveTaught: '',
    sixTaught: '',
    sevenTaught: '',
    eightTaught: '',
    nineMsTaught: '',
    nineTaught: '',
    tenTaught: '',
    elevenTaught: '',
    twelveTaught: '',
    onsiteTaughtDay: '',
    onsiteTaughtHour: '',
  },

  holdTestPrep: '',

  apCourseOffer: '',

  apCourseOfferConfirmation: '',

  apCourseOfferList: '',

  approvedAccreditation: {
    cbAccredited: '',
    cbAccreditedBy: '',
    agencyAccredited: '',
    agencyAccreditedName: '',
  },
  accreditationExpiry: '', // Flag to signal that an accreditation that is expired or will expire within the next 30 days
  teachers: {
    hsFullTimeTeachers: '',
    hsPartTimeTeachers: '',
    msFullTimeTeachers: '',
    msPartTimeTeachers: '',
  },
  relatives: {
    relativeEmployed: '',
    relativeEmployedAdministrators: '',
    relativeEmployedTeachers: '',
  },

  academicDiscipline: {
    academicDisciplineEnglish: '',
    academicDisciplineMatch: '',
    academicDisciplineHistory: '',
    academicDisciplineScience: '',
    academicDisciplineForeignLanguage: '',
    academicDisciplineOther: '',
    academicDisciplineOtherName: '',
  },

  collegeBoardOfferings: {
    preApCourses: '',
    administerPsat: '',
    satTestCenter: '',
  },

  administeredTest: {
    administeredTestAp: '',
    administeredTestPsat: '',
    administeredTestSat: '',
    administeredTestNone: '',
    administeredTestOther: '',
    administeredTestOtherName: '',
  },

  primaryTeach: {
    teachOnSite: '',
    teachOnline: '',
    teachEvening: '',
    teachHome: '',
    teachOther: '',
    teachOtherName: '',
  },

  security: {
    testAtVisitingAddress: '',
    testAtAddress: '',
    testMaterialReceivedBy: '',
    receivedMainOffice: '',
    receivedLoadingDock: '',
    receivedOther: '',
    receivedOtherName: '',
    materialStored: '',
    storageLocked: '',
    responsiblePerson: '',
    nonEmployedAccess: '',
    testingRoomsExist: '',
    testingRoomsPowerWifi: '',
  },

  disclaimer: '',
  submitter: '',
  categoryId: '',
  schoolresponse: null,
  loading: false,
  dosubmit: false,
  processStatus: '',
  addUpdated: false,
};
